import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { loadStripe, type Appearance, type StripeElementsOptions } from '@stripe/stripe-js'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { AppShell } from '../components/AppShell'
import { api, apiErrorMessage, useTiers, type Tier } from '../lib/api'

const PUBLIC_KEY = (import.meta.env.VITE_STRIPE_PUBLIC_KEY ?? '').trim()
const stripePromise = PUBLIC_KEY ? loadStripe(PUBLIC_KEY) : null

function fmtEur(v: string | number | undefined): string {
  if (v == null) return '—'
  const n = typeof v === 'string' ? Number(v) : v
  if (!Number.isFinite(n)) return '—'
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n)
}

function tierBenefits(tier: Tier | undefined): string[] {
  if (!tier) return []
  const accountSize = fmtEur(tier.accountSizeEur ?? tier.startingBalance)
  const split = tier.p4ProfitSplitTrader != null
    ? `${(tier.p4ProfitSplitTrader * 100).toFixed(0)}/${(100 - tier.p4ProfitSplitTrader * 100).toFixed(0)} profit split`
    : '—'
  const target = tier.p1TargetPct != null ? `+${(tier.p1TargetPct * 100).toFixed(0)}% P1 target` : '—'
  const days = tier.p1MaxDays != null ? `${tier.p1MaxDays}-day P1 window` : '—'
  return [
    `${accountSize} virtual starting balance`,
    target,
    days,
    split,
    'Pre-match major markets',
  ]
}

// Build the Stripe Elements appearance from RivalProp CSS variables.
// Read live from :root so dark/light toggle is reflected in the iframe.
function buildAppearance(): Appearance {
  const root = window.getComputedStyle(document.documentElement)
  const get = (name: string, fallback: string) => (root.getPropertyValue(name) || '').trim() || fallback
  return {
    theme: 'night',
    variables: {
      colorPrimary:      get('--color-primary', '#4d8bff'),
      colorBackground:   get('--bg-surface', '#131720'),
      colorText:         get('--text-primary', '#e7eaf0'),
      colorTextSecondary: get('--text-secondary', '#a8b0bd'),
      colorTextPlaceholder: get('--text-tertiary', '#7a8392'),
      colorDanger:       get('--color-danger-text', '#ff6b6b'),
      colorSuccess:      get('--color-success-text', '#22c55e'),
      colorIcon:         get('--text-secondary', '#a8b0bd'),
      colorIconTab:      get('--text-tertiary', '#7a8392'),
      colorIconTabSelected: get('--color-primary', '#4d8bff'),
      fontFamily:        get('--font-sans', 'Inter, system-ui, sans-serif'),
      fontSizeBase:      '14px',
      borderRadius:      '8px',
      spacingUnit:       '4px',
    },
    rules: {
      '.Input': {
        backgroundColor: get('--bg-canvas', '#0a0c10'),
        borderColor:     get('--border-subtle', '#1e2330'),
        boxShadow:       'none',
      },
      '.Input:focus': {
        borderColor:     get('--color-primary', '#4d8bff'),
        boxShadow:       `0 0 0 3px ${get('--color-primary-subtle', 'rgba(77,139,255,0.15)')}`,
      },
      '.Tab': {
        backgroundColor: get('--bg-canvas', '#0a0c10'),
        borderColor:     get('--border-subtle', '#1e2330'),
      },
      '.Tab--selected': {
        borderColor:     get('--color-primary', '#4d8bff'),
      },
      '.Label': {
        color: get('--text-secondary', '#a8b0bd'),
        fontWeight: '500',
      },
    },
  }
}

function PaymentForm({ amount, currency, tierLabel }: { amount: number; currency: string; tierLabel: string }) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setSubmitting(true)
    setError(null)
    const returnUrl = `${window.location.origin}/checkout-success`
    const { error: payErr } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    })
    // confirmPayment only resolves here on immediate validation errors;
    // a successful charge redirects to return_url before this line.
    setSubmitting(false)
    if (payErr) setError(payErr.message ?? 'Payment failed')
  }

  const fee = currency.toLowerCase() === 'eur'
    ? fmtEur(amount / 100)
    : `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <PaymentElement options={{ layout: 'tabs' }} />
      {error && (
        <div className="auth-card__error" role="alert" style={{ display: 'flex' }}>
          {error}
        </div>
      )}
      <button type="submit" className="btn btn--primary" disabled={!stripe || !elements || submitting}
        style={{ width: '100%', justifyContent: 'center', padding: 'var(--space-3)' }}>
        {submitting ? 'Processing…' : `Pay ${fee} for ${tierLabel}`}
      </button>
    </form>
  )
}

export function EmbeddedCheckoutPage() {
  const nav = useNavigate()
  const [params] = useSearchParams()
  const tierCode = (params.get('tier') ?? '').toLowerCase()
  const isReset  = params.get('reset') === '1'
  const parentAccountId = params.get('parent') ?? undefined

  const tiers = useTiers()
  const tier = tiers.data?.find((t) => t.code === tierCode)
  const fee = isReset
    ? Number(tier?.resetFeeEur ?? 0)
    : Number(tier?.feeEur ?? tier?.fee ?? 0)

  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [amount, setAmount]   = useState<number>(0)
  const [currency, setCurrency] = useState<string>('eur')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!PUBLIC_KEY) { setError('Stripe public key is not configured for this environment.'); return }
    if (!['starter', 'pro', 'elite'].includes(tierCode)) { setError(`Invalid tier "${tierCode}".`); return }
    let cancelled = false
    const run = async () => {
      try {
        const r = await api.post('/trader/checkout/payment-intent', { tierCode, isReset, parentAccountId })
        const data = (r.data as { data?: { clientSecret?: string; amount?: number; currency?: string } })?.data
        if (!data?.clientSecret) throw new Error('Missing clientSecret in response')
        if (cancelled) return
        setClientSecret(data.clientSecret)
        setAmount(data.amount ?? 0)
        setCurrency(data.currency ?? 'eur')
      } catch (e) {
        if (cancelled) return
        setError(apiErrorMessage(e))
      }
    }
    void run()
    return () => { cancelled = true }
  }, [tierCode, isReset, parentAccountId])

  const options = useMemo<StripeElementsOptions | null>(() => {
    if (!clientSecret) return null
    return { clientSecret, appearance: buildAppearance() }
  }, [clientSecret])

  const tierLabel = tier?.name ?? (tierCode.charAt(0).toUpperCase() + tierCode.slice(1))
  const benefits = tierBenefits(tier)

  return (
    <AppShell page="challenge" breadcrumb={<span className="topbar__breadcrumb-current">Checkout</span>}>
      <header className="page-header">
        <div className="page-header__main">
          <p className="page-header__eyebrow"><span>Checkout</span></p>
          <h1 className="page-header__title">{isReset ? `Reset ${tierLabel}` : `${tierLabel} Challenge`}</h1>
          <p className="page-header__subtitle">
            Pay securely with Stripe. Your challenge activates the moment Stripe confirms the payment.
          </p>
        </div>
        <div className="page-header__actions">
          <Link to="/challenge" className="btn btn--ghost btn--sm">← Back to tiers</Link>
        </div>
      </header>

      <div style={{
        display: 'grid', gap: 'var(--space-4)', marginTop: 'var(--space-3)',
        gridTemplateColumns: 'minmax(280px, 360px) 1fr',
      }} className="rp-checkout-grid">
        <aside style={{
          background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-lg)', padding: 'var(--space-5)',
          alignSelf: 'flex-start', position: 'sticky', top: 'var(--space-4)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
            <span className="tier-badge tier-badge--active">
              <span className="tier-badge__dot" aria-hidden />
              {tierLabel}
            </span>
            {isReset && <span className="chip">Reset</span>}
          </div>
          <div style={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)',
            color: 'var(--text-tertiary)', marginBottom: 'var(--space-1)' }}>Order total</div>
          <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 'var(--space-4)' }}>
            {fmtEur(fee)} <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--text-tertiary)' }}>one-time</span>
          </div>

          <div style={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)',
            color: 'var(--text-tertiary)', marginBottom: 'var(--space-2)' }}>What you get</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {benefits.map((b, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--color-success-text)" strokeWidth={2.5}
                  strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16, flexShrink: 0, marginTop: 2 }} aria-hidden>
                  <polyline points="5 13 10 18 19 7" />
                </svg>
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
            padding: 'var(--space-3)', background: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}
              strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16, flexShrink: 0 }} aria-hidden>
              <rect x="4" y="10" width="16" height="10" rx="2" />
              <path d="M8 10 V7 a4 4 0 0 1 8 0 v3" />
            </svg>
            <span>Secure payment processed by Stripe. We never store your card.</span>
          </div>
        </aside>

        <section>
          {error && (
            <div className="auth-card__error" role="alert" style={{ display: 'flex', marginBottom: 'var(--space-3)' }}>
              <span style={{ flex: 1 }}>{error}</span>
              <button type="button" className="btn btn--secondary btn--sm"
                onClick={() => nav('/challenge')}>Back</button>
            </div>
          )}

          {!error && !options && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              minHeight: 320, color: 'var(--text-tertiary)',
              background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-lg)',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
                  strokeLinecap="round" style={{ width: 18, height: 18, animation: 'rp-spin 0.9s linear infinite' }} aria-hidden>
                  <path d="M12 3 a9 9 0 1 0 9 9" />
                </svg>
                Loading secure payment form…
              </span>
              <style>{`@keyframes rp-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
            </div>
          )}

          {!error && options && stripePromise && (
            <div style={{
              background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-lg)', padding: 'var(--space-5)',
            }}>
              <Elements stripe={stripePromise} options={options}>
                <PaymentForm amount={amount} currency={currency} tierLabel={tierLabel} />
              </Elements>
            </div>
          )}
        </section>
      </div>

      <style>{`
        @media (max-width: 880px) {
          .rp-checkout-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </AppShell>
  )
}
